import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { GridFilterItem } from '@mui/x-data-grid';
import { PieChart, Pie, Tooltip } from 'recharts';
import { v4 } from 'uuid';
import { getProjectLoadingSubject, Project } from '../../../store/Project';
import { getCurrentPreferences, setPreferences, UserPreferences } from '../../../store/User';
import { generateProjectPipelineDoughnutData } from '../../../utils/helpers';
import { BORDER_STYLE } from '../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { NameType, Payload } from 'recharts/types/component/DefaultTooltipContent';

const excludedStatuses = ['Completed', 'Cancelled'];
const chartWidth = 250;
const chartHeight = 132;

export interface ProjectPipelineWidgetProps {
  projects: Project[];
  isHome?: boolean;
}

interface CategoricalChartState {
  name: string;
}

export const ProjectPipelineWidget = ({ projects, isHome }: ProjectPipelineWidgetProps) => {
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const pipelineProjects = projects.filter((project) => !excludedStatuses.includes(project.status));
  const data = generateProjectPipelineDoughnutData(pipelineProjects);

  useEffect(() => {
    const loadingSub = getProjectLoadingSubject().subscribe((loading) => {
      setProjectsLoading(loading);
    });

    return () => {
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const filterStatus = async (statusName: string) => {
    const filter: GridFilterItem = {
      id: v4(),
      field: 'product.nameAbbreviation',
      operator: 'is',
      value: statusName
    };

    const update: UserPreferences = JSON.parse(JSON.stringify(getCurrentPreferences()));
    if (update.content.project_grid_Active.muiConfig.filter?.filterModel) {
      update.content.project_grid_Active.muiConfig.filter.filterModel.items = [filter];
    }

    setPreferences(update);
    if (isHome) navigate('/projects');
  };

  const chartClick = (status: string) => () => {
    filterStatus(status);
  };

  const pieChartClick = (chartState: CategoricalChartState) => {
    filterStatus(chartState.name);
  };

  const correctData = data.filter((item) => item.value > 0);
  const isCorrectData = correctData.length > 0;
  const gridStyles = isHome ? 'w-full' : 'w-80 h-52';

  return (
    <div
      className={`${BORDER_STYLE} ${gridStyles} pt-3 pr-2 bg-white`}
      data-cy="status-widget">
      <div className="col-span-2 text-sm font-medium tracking-[0.6px] text-grey-5 text-center leading-[15px]">
        {pipelineProjects.length > 0 ? pipelineProjects.length : ''} Projects in the Pipeline
      </div>
      {!isCorrectData && !projectsLoading && (
        <div className={`flex ${isHome ? 'h-32' : 'h-44'} items-center justify-center`}>
          <TextBlock size="sm14">No data available</TextBlock>
        </div>
      )}
      {projectsLoading && (
        <div className={`flex ${isHome ? 'h-32' : 'h-44'} items-center justify-center`}>
          <ComponentSpinner />
        </div>
      )}
      {!projectsLoading && isCorrectData && (
        <div className="flex flex-col items-center w-full">
          <div className="text-sm widget-container w-full">
            <PieChart
              width={chartWidth}
              height={chartHeight}>
              <Pie
                dataKey="value"
                data={correctData}
                cy={100}
                cx={150}
                startAngle={180}
                endAngle={0}
                innerRadius={50}
                onClick={pieChartClick}
                minAngle={5}
                paddingAngle={5}
                outerRadius={80}></Pie>
              <Tooltip
                content={ProductTooltip}
                offset={-40}
              />
            </PieChart>
          </div>
          <div className="flex flex-row flex-wrap gap-x-1 px-2 w-full">
            {data.map((item) => (
              <StatusText
                color={item.fill}
                key={item.name}
                onClick={chartClick(item.name)}
                text={item.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusText = ({ text, color, onClick }: { text: string; color: string; onClick: () => void }) => {
  return (
    <div
      aria-label={text}
      className="mr-2"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      role="button"
      tabIndex={0}>
      <div className="flex flex-row gap-x-1 items-center mb-1">
        <span
          className="inline-flex w-2 h-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <TextSpan
          size="xs"
          className="text-ellipsis overflow-hidden whitespace-nowrap max-w-[100px]"
          weight="medium">
          {text}
        </TextSpan>
      </div>
    </div>
  );
};

const ProductTooltip = ({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Payload<number, NameType>[] | undefined;
  label?: string;
}) => {
  const isVisible = active && payload && payload.length;
  return (
    <div
      className="relative flex flex-row justify-start items-start rounded p-2 w-[68px] bg-[#64664C]"
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
      <div className="absolute left-[25px] top-[18px] w-[20px] h-[20px] bg-[#64664C] rotate-45"></div>
      {isVisible && (
        <div className="text-center w-full z-20">
          <TextSpan
            size="sm14"
            weight="semibold"
            color="white">
            {payload[0].value}
          </TextSpan>
        </div>
      )}
    </div>
  );
};
